import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Users, Plus, Pencil, Trash2, ArrowLeft, Search,
  TrendingUp, Factory, Shirt, Check, X, Eye, EyeOff, AlertTriangle
} from 'lucide-react';

const GestionUsuariosPage = () => {
  const navigate = useNavigate();
  const { user } = useApp();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    nombre: '',
    email: '',
    rol: 'USUARIO',
    acceso_finanzas: false,
    acceso_produccion: false,
    acceso_muestras: false
  });

  useEffect(() => {
    if (!user) return;
    if (user.rol !== 'ADMIN') {
      navigate('/');
      return;
    }
    loadUsuarios();
  }, [user, navigate]);

  const loadUsuarios = async () => {
    try {
      const res = await api.get('/usuarios');
      setUsuarios(res.data);
    } catch (error) {
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        await api.put(`/usuarios/${editingUser.id}`, formData);
        toast.success('Usuario actualizado');
      } else {
        await api.post('/usuarios', formData);
        toast.success('Usuario creado');
      }
      setModalOpen(false);
      resetForm();
      loadUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    try {
      await api.delete(`/usuarios/${deleteConfirm.id}`);
      toast.success('Usuario eliminado');
      setDeleteConfirm(null);
      loadUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  const openEditModal = (u) => {
    setEditingUser(u);
    setFormData({
      username: u.username,
      password: '',
      nombre: u.nombre,
      email: u.email || '',
      rol: u.rol,
      acceso_finanzas: u.acceso_finanzas,
      acceso_produccion: u.acceso_produccion,
      acceso_muestras: u.acceso_muestras
    });
    setModalOpen(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      password: '',
      nombre: '',
      email: '',
      rol: 'USUARIO',
      acceso_finanzas: false,
      acceso_produccion: false,
      acceso_muestras: false
    });
    setShowPassword(false);
  };

  const filteredUsers = usuarios.filter(u =>
    u.nombre.toLowerCase().includes(search.toLowerCase()) ||
    u.username.toLowerCase().includes(search.toLowerCase())
  );

  const apps = [
    { key: 'acceso_finanzas', icon: TrendingUp, label: 'Finanzas', color: '#4f46e5' },
    { key: 'acceso_produccion', icon: Factory, label: 'Producción', color: '#059669' },
    { key: 'acceso_muestras', icon: Shirt, label: 'Muestras', color: '#7c3aed' }
  ];

  const Toggle = ({ checked, onChange }) => (
    <button
      type="button"
      onClick={onChange}
      className={`relative w-10 h-5 rounded-full transition-colors ${checked ? 'bg-blue-500' : 'bg-gray-200'}`}
    >
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-5' : ''}`} />
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/')}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">Gestión de Usuarios</h1>
                <p className="text-sm text-gray-500">Administra accesos y permisos del sistema</p>
              </div>
            </div>
            <Button
              onClick={() => { resetForm(); setModalOpen(true); }}
              className="bg-blue-600 hover:bg-blue-700 h-9 px-4"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Usuario
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-6">
        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Buscar usuario..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 h-9 bg-white"
          />
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12 text-gray-500 text-sm">Cargando...</div>
        ) : (
          <table className="w-full bg-white rounded-xl shadow-sm overflow-hidden">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-6 py-3">Usuario</th>
                <th className="text-left text-xs font-medium text-gray-500 uppercase px-6 py-3">Rol</th>
                <th className="text-center text-xs font-medium text-gray-500 uppercase px-6 py-3">Accesos</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase px-6 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filteredUsers.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50/50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 font-medium text-sm">
                        {u.nombre?.[0]?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{u.nombre}</p>
                        <p className="text-xs text-gray-500">@{u.username}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${
                      u.rol === 'ADMIN' 
                        ? 'bg-blue-50 text-blue-700' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {u.rol}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-4">
                      {apps.map((app) => (
                        <div
                          key={app.key}
                          className="flex items-center gap-1.5"
                          title={app.label}
                        >
                          <app.icon 
                            className="w-4 h-4" 
                            style={{ color: u[app.key] ? app.color : '#d1d5db' }}
                          />
                          {u[app.key] ? (
                            <Check className="w-3.5 h-3.5 text-green-500" />
                          ) : (
                            <X className="w-3.5 h-3.5 text-gray-300" />
                          )}
                        </div>
                      ))}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEditModal(u)}
                        className="p-1.5 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                        title="Editar"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      {u.id !== user.id && (
                        <button
                          onClick={() => setDeleteConfirm(u)}
                          className="p-1.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        
        {!loading && filteredUsers.length === 0 && (
          <div className="text-center py-12 text-gray-500 text-sm">
            No se encontraron usuarios
          </div>
        )}
      </main>

      {/* Create/Edit User Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">
              {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs text-gray-600">Nombre completo</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Nombre"
                  className="h-9 mt-1"
                  required
                />
              </div>
              <div>
                <Label className="text-xs text-gray-600">Usuario</Label>
                <Input
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="username"
                  className="h-9 mt-1"
                  required
                />
              </div>
            </div>

            <div>
              <Label className="text-xs text-gray-600">Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@ejemplo.com"
                className="h-9 mt-1"
              />
            </div>

            <div>
              <Label className="text-xs text-gray-600">
                Contraseña {editingUser && '(dejar vacío para no cambiar)'}
              </Label>
              <div className="relative mt-1">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  className="h-9 pr-10"
                  required={!editingUser}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label className="text-xs text-gray-600">Rol</Label>
              <select
                value={formData.rol}
                onChange={(e) => setFormData({ ...formData, rol: e.target.value })}
                className="w-full h-9 mt-1 px-3 border rounded-md text-sm bg-white"
              >
                <option value="USUARIO">Usuario</option>
                <option value="ADMIN">Administrador</option>
              </select>
            </div>

            {/* App Access Toggles */}
            <div className="pt-2 border-t border-gray-100">
              <Label className="text-xs text-gray-600 mb-3 block">Acceso a Aplicaciones</Label>
              <div className="space-y-3">
                {apps.map((app) => (
                  <div key={app.key} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <app.icon className="w-4 h-4" style={{ color: app.color }} />
                      <span className="text-sm text-gray-700">{app.label}</span>
                    </div>
                    <Toggle
                      checked={formData[app.key]}
                      onChange={() => setFormData({ ...formData, [app.key]: !formData[app.key] })}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => setModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                {editingUser ? 'Guardar' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="max-w-sm">
          <div className="flex flex-col items-center text-center py-4">
            <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">¿Eliminar usuario?</h3>
            <p className="text-sm text-gray-500 mb-6">
              Esta acción eliminará permanentemente a <strong>{deleteConfirm?.nombre}</strong> (@{deleteConfirm?.username}).
            </p>
            <div className="flex gap-3 w-full">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setDeleteConfirm(null)}
                disabled={deleting}
              >
                Cancelar
              </Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GestionUsuariosPage;
